import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectRenderer, SelectState } from 'react-dropdown-select';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import orderBy from 'lodash/orderBy';
import styled, { css } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { Icon } from 'components';
import Select from 'components/Select/Select';
import { ISelectOption } from 'components/Select/types';
import { theme } from 'design';
import { toRem } from 'design/utils';
import { getJobTitlesRequest, selectJobTitlesBySite } from 'store/Collections';
import { IJobTitleState } from 'store/types';

interface IProps {
  additionalJobTitles?: IJobTitleState[];
  className?: string;
  dataCy?: string;
  defaultValue?: ISelectOption<IJobTitleState>;
  disabled?: boolean;
  includeInactive?: boolean;
  isLoading?: boolean;
  onChange(selectedOption: ISelectOption<IJobTitleState>): void;
  placeholder?: string;
  siteId: string;
}

interface IJobTitleSelectHandle {
  dropdown: boolean;
}

const StyledJobTitleSelect = styled(Select)`
  .react-dropdown-select-content {
    min-height: ${toRem(32)};

    span {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      max-width: calc(100% - 38px);
      height: auto;
      word-wrap: break-word;
      overflow: hidden;
      line-height: ${toRem(16)};
      max-height: 100%;
    }
  }

  .react-dropdown-select-dropdown {
    top: calc(100% + 10px);

    & > div {
      max-width: 100%;
      height: auto;

      & > div {
        display: -webkit-box;
        height: auto;
        min-height: ${toRem(26)};
        overflow-wrap: anywhere;
        -webkit-line-clamp: 2;
      }
    }
  }
`;

interface DropdownHandleProps {
  isOpen?: boolean;
  left: number;
}
const DropdownHandle = styled.div.attrs({
  className: 'drop-handle',
})<DropdownHandleProps>`
  position: absolute;
  top: 0;
  left: ${({ left }) => `${left - 10}px`};
  justify-content: center;
  align-items: center;
  width: ${toRem(38.5)};
  height: 100%;
  border-radius: ${toRem(5.5)};

  svg {
    fill: ${theme.color.neutral[800]};
    transition: transform ${theme.transition.icon};
    width: ${toRem(6)} !important;
    height: ${toRem(4)} !important;
  }

  &:hover {
    path {
      stroke: none !important;
    }
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
`;

const Loader = styled(Skeleton)`
  display: flex;
  width: ${toRem(77)}!important;
  height: ${toRem(13.25)};
`;

const getJobTitleWidth = (id: string) => {
  const select = document.querySelector(`#${id} .react-dropdown-select-content.react-dropdown-select-type-single span`) as HTMLElement;
  return select.offsetWidth;
};

export const JobTitleSelect: React.FC<IProps> = ({ additionalJobTitles = [], className = '', dataCy, defaultValue, disabled, includeInactive, isLoading, onChange, placeholder, siteId }) => {
  const dispatch = useDispatch();
  const jobTitles = useSelector(selectJobTitlesBySite(siteId));
  const id = useRef(`job-title-select-${uuidv4()}`).current;
  const [jobTitleWidth, setJobTitleWidth] = useState<number>(0);

  useEffect(() => {
    if (!siteId) return;
    if (!jobTitles) dispatch(getJobTitlesRequest({ siteId, includeInactive }));
    if (!jobTitleWidth) setJobTitleWidth(getJobTitleWidth(id));
  }, [siteId]);

  const allJobTitles = useMemo(() => [...(jobTitles || []), ...(additionalJobTitles || [])], [jobTitles, additionalJobTitles]);

  const options = useMemo(
    () =>
      orderBy(
        allJobTitles?.map((jobTitle) => ({
          label: jobTitle.title,
          value: jobTitle,
        })),
        [(job) => job.label.toLowerCase()],
        ['asc']
      ),
    [allJobTitles]
  );

  const onJobTitleChange = useCallback(
    (items: ISelectOption<IJobTitleState>[]) => {
      onChange(items[0]);
      setJobTitleWidth(getJobTitleWidth(id));
    },
    [onChange]
  );

  const dropdownHandleRenderer = ({ state }: SelectRenderer<SelectState<IJobTitleSelectHandle>>) => (
    <DropdownHandle isOpen={state.dropdown} left={jobTitleWidth}>
      <Icon icon="Carrot" />
    </DropdownHandle>
  );

  return isLoading ? (
    <Loader />
  ) : (
    <StyledJobTitleSelect
      id={id}
      className={className}
      dataCy={dataCy}
      disabled={disabled}
      onChange={onJobTitleChange}
      options={options}
      placeholder={placeholder}
      values={defaultValue ? [defaultValue] : []}
      dropdownHandleRenderer={dropdownHandleRenderer}
    />
  );
};
